// /**
//  * Warehouses - list
//  */

// React
import React, {useContext} from 'react';
import {Link} from 'react-router-dom';

// UI
import theme from '../../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton, Grid, TooltipProps, Tooltip,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiClose,
  mdiPencilOutline,
  mdiFileDocumentOutline,
  mdiCheck, mdiAlertCircle,
} from '@mdi/js';

// Type
import {warehouseZoneAisles} from '../interfaces';
import AuthContext from '../../../authContext';
import Can from '../../../components/Can';

type Data = warehouseZoneAisles;
type Props = {
  data: any;
  handleOpenConfirmActive: (id: number) => void;
  handleOpenConfirmDisable: (id: number) => void;
  handleOpenEditModal: (id: number) => void;
};

type Ord = 'asc' | 'desc';

interface HeadCell {
  id: any;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    body: {
      fontSize: 14,
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    errorChip: {
      backgroundColor: '#fdadb6',
      color: '#fa2b2b',
      padding: '2px 5px',
      borderRadius: '5px',
      margin: 'auto 10px',
    },
    warningChip: {
      margin: '2px 10px',
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

const useStylesBootstrap = makeStyles((theme: Theme) => ({
  arrow: {
    color: theme.palette.common.black,
  },
  tooltip: {
    backgroundColor: theme.palette.common.black,
  },
}));

function BootstrapTooltip(props: TooltipProps) {
  const classes = useStylesBootstrap();

  return <Tooltip arrow classes={classes} {...props} />;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const headCells: HeadCell[] = [
  {
    id: 'name',
    label: 'شماره راهرو',
  },
  {
    id: 'columnCount',
    label: 'تعداد ستون',
  },
  {
    id: 'rowCount',
    label: 'تعداد طبقه',
  },
  {
    id: 'unitCount',
    label: 'تعداد یونیت',
  },
];

function EnhancedTableHead(props: EnhancedTableProps) {
  const {order, orderBy, onRequestSort} = props;
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell />
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<any>('id');
  const [dense] = React.useState(false);
  const {
    handleOpenConfirmDisable,
    handleOpenEditModal,
    handleOpenConfirmActive,
  } = props;

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow key={index}>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      <span
                        className={classes.dot}
                        style={{
                          backgroundColor:
                            row.status === 'UNAVAILABLE'
                              ? theme.palette.error.main
                              : theme.palette.green.main,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      <span>{row.name}</span>
                      {row.wastageUnitCount === row.unitCount && (
                        <span className={classes.errorChip}>{'ضایعات'}</span>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Grid alignItems="center" justify="center" container>
                        <span>{row.columnCount}</span>
                        {row.wastageUnitCount &&
                        row.wastageUnitCount !== row.unitCount && (
                          <span className={classes.warningChip}>
                            <BootstrapTooltip title="یک یا چند ستون به عنوان انبار ضایعات تعریف شده است">
                              <Icon path={mdiAlertCircle} color="#f69f1d" size={1} />
                            </BootstrapTooltip>
                          </span>
                        )}
                      </Grid>
                    </TableCell>
                    <TableCell align="center">{row.rowCount}</TableCell>
                    <TableCell align="center">{row.unitCount}</TableCell>
                    <TableCell align="right">
                      <Link to={`/warehouse-zones/column/${row.id}?name=${row.name}`}>
                        <IconButton
                          aria-label="list"
                          size="small"
                          title="لیست ستون‌ها"
                          data-cy="list"
                        >
                          <Icon
                            path={mdiFileDocumentOutline}
                            size={1}
                            color={theme.palette.green.main}
                          />
                        </IconButton>
                      </Link>
                      <Can
                        perform="warehouse-zone-aisles:edit"
                        role={authContext.user.role}
                        yes={() => (
                          <IconButton
                            title="ویرایش"
                            size="small"
                            onClick={() => handleOpenEditModal(row.id)}
                          >
                            <Icon path={mdiPencilOutline} size={1} />
                          </IconButton>
                        )}
                      />
                      <Can
                        perform="warehouse-zone-aisles:changeStatus"
                        role={authContext.user.role}
                        yes={() =>
                          row.status === 'AVAILABLE' ? (
                            <IconButton
                              aria-label="disable"
                              title="غیرفعال کردن زون"
                              size="small"
                              onClick={() => handleOpenConfirmDisable(row.id)}
                            >
                              <Icon
                                path={mdiClose}
                                size={1}
                                color={theme.palette.error.main}
                              />
                            </IconButton>
                          ) : (
                            <IconButton
                              aria-label="active"
                              title="فعال کردن زون"
                              size="small"
                              onClick={() => handleOpenConfirmActive(row.id)}
                            >
                              <Icon
                                path={mdiCheck}
                                size={1}
                                color={theme.palette.green.main}
                              />
                            </IconButton>
                          )
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
